/**
 * The Club calendar fixture (`?clubState=calendarClub`, lane swim-club-calendar
 * 2026-09-12): a six-swimmer club over August 2026 with shared days of 2, 3 and
 * 6 swimmers, one 12-swimmer day (the six regulars plus six guests), several
 * one-swimmer days, a member with two swims on one day, and a 23:55 swim the
 * morning before someone else's 06:10.
 *
 * ONE file feeds both halves of the Club tab so they never disagree on screen:
 * the session source (calendar + day card) and the C5 snapshot (members strip,
 * the 14 days ending on `generatedAt`).
 *
 * Dates are LOCAL wall-clock ISO with no offset, unlike the C5 JSON fixtures'
 * 06:xx-06:00 pin (README): an offset cannot keep a 23:55 swim on its day in
 * every test zone, while an offsetless date-time parses as local time anywhere.
 */
import type { MemberSwim } from "../../clubSessions";
import fixture from "./calendar-club-month.json";

interface FixtureSwim {
  member: string;
  id: string;
  date: string;
  distanceM: number;
}

const SWIMS: FixtureSwim[] = fixture.swims;
const NAMES: Record<string, string> = Object.fromEntries(fixture.members.map((m) => [m.id, m.name]));
const WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

/** About 40 s per 25 m, a little slower per member in fixture order, so a shared
 *  day's roster never shows the same time three times. Realism only. */
const MEMBER_ORDER = fixture.members.map((m) => m.id);
const durationOf = (s: FixtureSwim) => Math.round(s.distanceM * 1.6) + Math.max(0, MEMBER_ORDER.indexOf(s.member)) * 37;

export const CALENDAR_CLUB_SELF = fixture.self;

export function calendarClubLabel(id: string): string {
  return NAMES[id] ?? id;
}

/** Every member's swims as the session source hands them to groupSessions(). */
export function calendarClubMemberSwims(): MemberSwim[] {
  return SWIMS.map((s) => ({
    memberId: s.member,
    swim: {
      uuid: s.id,
      startDate: s.date,
      endDate: s.date,
      activityType: "swimming",
      durationSeconds: durationOf(s),
      totalDistance: { value: s.distanceM, unit: "m" },
      swimmingLocationType: "pool",
    },
  }));
}

/** The clubmates (everyone but self) as listCompanions would return them. */
export function calendarClubCompanions(): { id: string; name: string; count: number; last: string }[] {
  return fixture.members
    .filter((m) => m.id !== fixture.self)
    .map((m) => {
      const mine = SWIMS.filter((s) => s.member === m.id);
      return { id: m.id, name: m.name, count: mine.length, last: mine[mine.length - 1]?.date ?? fixture.generatedAt };
    });
}

/** The C5 `club:snapshot` body for the same club, shaped like native's: the viewer's own swims
 *  cut to the 14 days ending on generatedAt (ClubSnapshotSender.fetchSelfSwims), every
 *  clubmate's stored swims in full (B-016). Build 15: the day roster reads C5, so a clubmate
 *  day outside the 14 days (Dana's Aug 14 23:55) needs its swim here. weekTotals is the Monday
 *  week (Mon Aug 31 00:00 through generatedAt), which holds no swims. */
export function calendarClubSnapshotBody(): unknown {
  const gen = Date.parse(fixture.generatedAt);
  return {
    v: 1,
    generatedAt: fixture.generatedAt,
    self: fixture.self,
    members: fixture.members.map((m) => ({
      id: m.id,
      name: m.name,
      swims: SWIMS.filter((s) => s.member === m.id && Date.parse(s.date) <= gen && (m.id !== fixture.self || gen - Date.parse(s.date) < WINDOW_MS)).map((s) => ({
        id: s.id,
        date: s.date,
        distanceM: s.distanceM,
        durationSec: durationOf(s),
        poolM: 25,
        sets: [],
      })),
      weekTotals: { swims: 0, distanceM: 0, durationSec: 0 },
    })),
    reactions: [],
  };
}
